import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CardActions,
  Switch,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DialogComponent } from "./DialogComponent";
import { useContext, useState } from "react";
import tickCircle from '../../../assets/images/userOnboarding/tick-circle.svg';
import playCircle from '../../../assets/images/userOnboarding/play-circle.svg';
import setting from '../../../assets/images/userOnboarding/setting-2.svg';
import VideoOverlay from "./VideoOverlay";
import { userOnboardingContext } from "../../../states/context/userOnboardingContext";

const StyledCard = styled(Card)({
  borderRadius: '8px',
  border: '1px solid var(--eerie-black-tint-6, #E8E8E8)',
  background: '#FFF',
  boxShadow: 'none',
});

const StyledCardContent = styled(CardContent)({
  padding: '24px 24px 0px 24px',
});

const BaseChip = styled(Chip)({
  textAlign: 'right',
  fontFamily: 'Figtree',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
  textTransform: 'uppercase',
  borderRadius: '4px',
});

const NewChip = styled(BaseChip)({
  marginRight: '4px',
  backgroundColor: '#E8F5F6',
  color: '#1A99A3',
});

const CategoryChip = styled(BaseChip)({
  backgroundColor: '#E8E8E8',
  color: '#747474',
});

const StyledTitle = styled(Typography)({
  color: '#171717',
  fontFamily: 'Figtree',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: '600',
  lineHeight: 'normal',
  margin: '9.5px 0 8px 0',
});

const StyledDescription = styled(Typography)({
  color: '#737373',
  fontFamily: 'Figtree',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
});

const SolveChallengesText = styled(Typography)({
  color: '#1A99A3',
  fontFamily: 'Figtree',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
  marginLeft: '4px',
  marginTop: '16px 0 16.5px 0',
});

const SwitchContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  margin: '16px 0 24.5px 0',
});

const EnableText = styled(Typography)({
  color: '#737373',
  fontFamily: 'Figtree',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
});

const StyledButton = styled(Button)({
  backgroundColor: '#F0EBFF',
  flex: 1,
  padding: '14.5px 28px',
  display: 'flex',
  color: '#6933FF',
  fontFamily: 'Figtree',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '600',
  lineHeight: 'normal',
  textTransform: 'none',
  position: 'relative',
  '&:hover': {
    backgroundColor: '#F0EBFF',
  },
  '@media (max-width: 1350px)': {
    fontSize: '14px',
  },
});

const CustomizeButton = styled(StyledButton)({
  borderRadius: '0px 0px 0px 7px',
  '&::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    height: '100%',
    width: '1px',
    backgroundColor: '#E1D6FF',
  },
});

const HowItWorksButton = styled(StyledButton)({
  borderRadius: '0px 0px 7px 0px',
});

const StyledCardActions = styled(CardActions)({
  padding: 0,
  margin: 0,
  '& > :not(:first-child)': {
    marginLeft: 0,
  },
});

const StyledSwitch = styled(Switch)({
  '& .MuiSwitch-track': {
    backgroundColor: 'grey',
  },
  '& .MuiSwitch-switchBase': {
    '&.Mui-disabled': {
      color: '#e886a9',
    },
    '&.Mui-checked': {
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#4CAF50',
      },
    },
  },
  '& .MuiSwitch-colorPrimary': {
    '&.Mui-checked': {
      color: '#4CAF50',
      '& + .MuiSwitch-track': {
        backgroundColor: '#4CAF50',
      },
    },
  },
});

const AutomationCard = ({ id, title, description, category, solves, enabled, automationType }) => {
  const { user,setUser } = useContext(userOnboardingContext);
  const { recommendedAutomations, relevantAutomations } = user;

  const [openDialog, setOpenDialog] = useState(false);
  const [isOverlayOpen, setOverlayOpen] = useState(false);

  const handleVideoOverlay = () => {
    setOverlayOpen(true);
  };
  const closeOverlay = () => setOverlayOpen(false);

  const handleCustomizeModal = () => {
    setOpenDialog(true);
  };

  const handleEnable = (e) => {
    if (automationType === "recommended") {
      const automationEnabledUpdated = recommendedAutomations.map((item) =>
        item.id == id ? { ...item, enabled: !item.enabled } : item
      );
      setUser({
        ...user,
        recommendedAutomations: automationEnabledUpdated,
      });
      return;
    }
    const relevantAutomationEnabled = relevantAutomations.map((item) =>
      item.id == id ? { ...item, enabled: !item.enabled } : item
    );
    setUser({
      ...user,
      relevantAutomations: relevantAutomationEnabled,
    });
    return;
  };

  return (
    <>
    <StyledCard>
      <StyledCardContent>
        <Box display="flex" mb={1}>
          <NewChip label="NEW" size="small" />
          <CategoryChip label={category} size="small" />
        </Box>
        <StyledTitle variant="h4">
          {title}
        </StyledTitle>
        <StyledDescription variant="body1">
          {description}
        </StyledDescription>
        <Box display="flex" alignItems="center" mt={2}>
          <img
            src={tickCircle}
            alt="tickCircle"
          />
          <SolveChallengesText variant="body2">
            Solves {solves} challenges you face
          </SolveChallengesText>
        </Box>
        <SwitchContainer>
          <StyledSwitch 
            checked={enabled}
            onChange={handleEnable}
          />
          <EnableText variant="body1">
            Enable
          </EnableText>
        </SwitchContainer>
      </StyledCardContent>
      <StyledCardActions>
          <CustomizeButton onClick={handleCustomizeModal}>
            <img
              src={setting}
              alt="setting"
              style={{ marginRight: '4px' }}
            />
            Customize
          </CustomizeButton>
          <HowItWorksButton onClick={handleVideoOverlay}>
            <img
              src={playCircle}
              alt="playCircle"
              style={{ marginRight: '4px' }}
            />
            How it works
          </HowItWorksButton>
        </StyledCardActions>
      </StyledCard>
      {isOverlayOpen && <VideoOverlay
        videoId="NdygTNST4l8"
        isOpen={isOverlayOpen}
        onClose={closeOverlay}
      />}
      {openDialog && (
        <DialogComponent
          open={openDialog}
          close={setOpenDialog}
          title={title} // currently sending title to store the input box value into the context
          // with respect to title. It will be changed with the id when api is ready.
        />
      )}
    </>
  );
};

export default AutomationCard;